/** Opens a history Job in the existing client dialog without leaving the current board or changing its URL. */
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Entity, ServiceHistoryRow } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService, WorkBuddyDialogService } from '@workbuddy/ui/services';
import { ButtonLinkComponent } from '@workbuddy/ui/v2/components/button';
import { ClientPortalContextService } from '@workbuddy/ui/modules/client.manage.job/src/services/client.portal.context.service';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { canOpenPortalJobs, openPortalJobDialog } from '../../shared/portal.job.dialog';

/** Portal-only Job link; the server rechecks both Job permission and linked customer ownership. */
@Component({
  selector: 'portal-history-job-cell',
  standalone: true,
  imports: [ButtonLinkComponent],
  providers: [WorkBuddyDialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: flex; align-items: center; min-width: 0; height: 100%;' },
  template: `
    @if ($job(); as job) {
      @if ($allowed()) {
        <wb-button-link displayStyle="Primary" [label]="job.name ?? 'Job'" [disabled]="$loading()" (onClick)="open($event)" />
      } @else {
        <span>{{ job.name }}</span>
      }
    } @else {
      <span>Not set</span>
    }
  `,
})
export class PortalHistoryJobCellRenderer implements ICellRendererAngularComp {
  private readonly data = inject(EntityDataServiceRaw);
  private readonly dialogs = inject(WorkBuddyDialogService);
  private readonly rights = inject(RightsService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly $customer = toSignal(inject(ClientPortalContextService).selectedEntity$);
  private dialog?: DynamicDialogRef;
  private dialogCustomerId?: string;
  protected readonly $job = signal<Entity | null>(null);
  protected readonly $loading = signal(false);
  protected readonly $allowed = computed(() => canOpenPortalJobs(this.rights));

  public constructor() {

    effect(() => {
      if (this.$customer()?._id !== this.dialogCustomerId) this.dialog?.close();
    });
    this.destroyRef.onDestroy(() => this.dialog?.close());
  }

  /** AG Grid supplies the Job reference already displayed in Task history. */
  public agInit(params: ICellRendererParams<ServiceHistoryRow>): void {

    this.$job.set(params.value ?? params.data?.job ?? null);
  }

  /** Reuses the cell while keeping the Job identity current after a history refresh. */
  public refresh(params: ICellRendererParams<ServiceHistoryRow>): boolean {

    this.agInit(params);
    return true;
  }

  /** Reads the authorised Job, then opens the existing viewer over the current Services tab. */
  public async open(event: Event): Promise<void> {

    event.preventDefault();
    event.stopPropagation();
    const jobId = this.$job()?._id;
    const customerId = this.$customer()?._id;
    if (!jobId || !customerId || !this.$allowed() || this.$loading() || this.dialog) return;
    this.$loading.set(true);
    try {
      const dialog = await openPortalJobDialog({ data: this.data, dialogs: this.dialogs, toast: this.toast }, jobId, () => !this.destroyRef.destroyed && this.$customer()?._id === customerId && this.$job()?._id === jobId);
      if (!dialog) return;
      this.dialogCustomerId = customerId;
      this.dialog = dialog;
      dialog.onClose
        .pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          if (this.dialog === dialog) this.dialog = undefined;
        });
    } finally {
      this.$loading.set(false);
    }
  }
}
